import { cn } from '@/utils/cn'
import { DIFFICULTY_LABELS, type DifficultyLevel } from '@/types/reading'

/**
 * The CEFR level of a text, as a filled chip on a seven-step ramp.
 *
 * Difficulty is the one thing this badge exists to say, so it may not look the same
 * at A0 and C2. Hue does the grading — emerald, green, olive, honey, ochre, heather,
 * deep heather — and the level is always written on the chip, so the sequence is
 * legible to someone who cannot separate those hues.
 *
 * Use this anywhere a level appears; the fills live in `--color-cefr-*` so the ramp
 * is defined once and flips with the theme.
 */

const FILLS: Record<DifficultyLevel, string> = {
  a0: 'bg-cefr-a0-bg',
  a1: 'bg-cefr-a1-bg',
  a2: 'bg-cefr-a2-bg',
  b1: 'bg-cefr-b1-bg',
  b2: 'bg-cefr-b2-bg',
  c1: 'bg-cefr-c1-bg',
  c2: 'bg-cefr-c2-bg',
}

interface CefrBadgeProps {
  level: DifficultyLevel
  /** Spell the level out ("A1 – Elementary") instead of just "A1". */
  withLabel?: boolean
  className?: string
}

export function CefrBadge({ level, withLabel = false, className }: CefrBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full whitespace-nowrap',
        'font-mono uppercase tracking-widest text-cefr-text',
        withLabel ? 'px-3 py-1 text-xs tracking-[0.12em]' : 'px-2.5 py-1 text-[11px]',
        FILLS[level],
        className,
      )}
    >
      {withLabel ? DIFFICULTY_LABELS[level] : level.toUpperCase()}
    </span>
  )
}
